<script setup lang="ts">
// Risk-tab line chart on lightweight-charts (2026-10-23). Replaces the three
// hand-drawn 600x200 SVGs (Rolling Sharpe, Portfolio Drawdown, Win Rate):
//   - the y-axis fits the data in view (a 3% drawdown fills the chart instead
//     of sitting in a sliver of a fixed 0–20% axis), with round ticks
//   - every series shows its last value as a labelled marker on the right-hand
//     price scale, always on screen; the legend repeats it as text
//   - a crosshair reads out date + each series' value wherever you hover
//   - the baseline is a dashed price line with its own axis label
//   - the whole history is loaded; the chart opens on the last `defaultWindow`
//     points, drag scrolls back, wheel zooms, double-click resets the window.
// An `area` series is a BaselineSeries anchored at 0 so a negative-percent
// drawdown fills between the line and the zero line (red), like the old SVG.
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import {
  createChart, createSeriesMarkers, LineSeries, BaselineSeries, CrosshairMode, LineStyle,
  type IChartApi, type ISeriesApi, type IPriceLine, type SeriesType, type ISeriesMarkersPluginApi, type SeriesMarker, type Time,
} from 'lightweight-charts'
import { defaultLogicalRange, formatRiskValue, DEFAULT_WINDOW_DAYS, type RiskPoint, type RiskValueFormat } from './riskChartSeries'

export interface RiskChartSeries {
  id: string
  label: string
  color: string
  data: RiskPoint[]
  lineWidth?: 1 | 2 | 3 | 4
  dashed?: boolean
  /** Fill between the line and zero (drawdown). */
  area?: boolean
}

/** Short label drawn on the first series at the first of its points on/after `time`. */
export interface RiskChartMarker { time: string; text: string; color?: string; muted?: boolean }

export interface RiskChartBaseline { value: number; label?: string; color?: string }

const props = withDefaults(defineProps<{
  series: RiskChartSeries[]
  baseline?: RiskChartBaseline | null
  markers?: RiskChartMarker[]
  format?: RiskValueFormat
  defaultWindow?: number
  height?: number
}>(), { baseline: null, format: 'number2', defaultWindow: DEFAULT_WINDOW_DAYS, height: 220 })

const chartEl = ref<HTMLDivElement | null>(null)
let chart: IChartApi | null = null
const apis: ISeriesApi<SeriesType>[] = []
let baselineLine: IPriceLine | null = null
let markersApi: ISeriesMarkersPluginApi<Time> | null = null
let resizeObserver: ResizeObserver | null = null

const tooltip = ref<{ visible: boolean; x: number; y: number; time: string; rows: { label: string; color: string; text: string }[] }>({
  visible: false, x: 0, y: 0, time: '', rows: [],
})

const fmt = (v: number) => formatRiskValue(v, props.format)

const legend = computed(() => props.series.map(s => ({
  id: s.id, label: s.label, color: s.color,
  last: s.data.length ? fmt(s.data[s.data.length - 1].value) : '—',
})))

function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex)
  if (!m) return hex
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${alpha})`
}

function longest(): number {
  return props.series.reduce((n, s) => Math.max(n, s.data.length), 0)
}

function resetWindow() {
  const range = defaultLogicalRange(longest(), props.defaultWindow)
  if (chart && range) chart.timeScale().setVisibleLogicalRange(range)
}

function build() {
  if (!chart) return
  apis.length = 0
  for (const s of props.series) {
    const common = {
      color: s.color,
      lineWidth: s.lineWidth ?? 1,
      lineStyle: s.dashed ? LineStyle.Dashed : LineStyle.Solid,
      lastValueVisible: true,
      priceLineVisible: false,
      title: s.label,
    }
    const api = s.area
      ? chart.addSeries(BaselineSeries, {
          ...common,
          baseValue: { type: 'price', price: 0 },
          topLineColor: s.color, topFillColor1: hexToRgba(s.color, 0.25), topFillColor2: hexToRgba(s.color, 0.02),
          bottomLineColor: s.color, bottomFillColor1: hexToRgba(s.color, 0.02), bottomFillColor2: hexToRgba(s.color, 0.25),
        })
      : chart.addSeries(LineSeries, common)
    apis.push(api as ISeriesApi<SeriesType>)
  }
  markersApi = null // the plugin belongs to the previous first series
  setData()
  resetWindow()
}

function applyMarkers() {
  const first = apis[0]
  const wanted = props.markers ?? []
  if (!first || (!wanted.length && !markersApi)) return
  const times = (props.series[0]?.data ?? []).map(p => p.time)
  const out: SeriesMarker<Time>[] = []
  for (const m of wanted) {
    const t = times.find(x => x >= m.time)
    if (!t) continue
    out.push({
      time: t as Time, position: 'aboveBar', shape: 'circle', text: m.text,
      color: m.color ?? (m.muted ? '#777777' : '#ffb74d'),
    })
  }
  out.sort((a, b) => String(a.time).localeCompare(String(b.time)))
  if (markersApi) markersApi.setMarkers(out)
  else if (out.length) markersApi = createSeriesMarkers(first as ISeriesApi<SeriesType, Time>, out)
}

function setData() {
  props.series.forEach((s, i) => apis[i]?.setData(s.data))
  applyMarkers()
  const first = apis[0]
  if (!first) return
  if (baselineLine) { first.removePriceLine(baselineLine); baselineLine = null }
  if (props.baseline) {
    baselineLine = first.createPriceLine({
      price: props.baseline.value,
      color: props.baseline.color ?? '#2196f3',
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: true,
      title: props.baseline.label ?? 'baseline',
    })
  }
}

onMounted(() => {
  if (!chartEl.value) return
  chart = createChart(chartEl.value, {
    width: chartEl.value.clientWidth || 600,
    height: props.height,
    layout: { background: { color: '#1a1a1a' }, textColor: '#888', fontFamily: 'JetBrains Mono, monospace', fontSize: 10 },
    grid: { vertLines: { color: '#262626' }, horzLines: { color: '#262626' } },
    rightPriceScale: { borderColor: '#333', scaleMargins: { top: 0.12, bottom: 0.12 } },
    timeScale: { borderColor: '#333', timeVisible: false, secondsVisible: false, rightOffset: 2 },
    crosshair: {
      mode: CrosshairMode.Normal,
      vertLine: { color: '#555', width: 1, style: LineStyle.Dashed, labelBackgroundColor: '#1c2128' },
      horzLine: { color: '#555', width: 1, style: LineStyle.Dashed, labelBackgroundColor: '#1c2128' },
    },
    localization: { priceFormatter: fmt },
  })
  build()

  chart.subscribeCrosshairMove(param => {
    if (!param.time || !param.point) { tooltip.value.visible = false; return }
    const rows = props.series.flatMap((s, i) => {
      const pt = apis[i] ? (param.seriesData.get(apis[i]) as { value?: number } | undefined) : undefined
      return pt && typeof pt.value === 'number' ? [{ label: s.label, color: s.color, text: fmt(pt.value) }] : []
    })
    if (!rows.length) { tooltip.value.visible = false; return }
    tooltip.value = { visible: true, x: param.point.x, y: param.point.y, time: String(param.time), rows }
  })

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (chart && chartEl.value && chartEl.value.clientWidth > 0) chart.applyOptions({ width: chartEl.value.clientWidth })
    })
    resizeObserver.observe(chartEl.value)
  }
})

// Series identities are fixed per chart (same count and order); only the data changes.
watch(() => props.series, () => {
  if (!chart) return
  if (apis.length !== props.series.length) { apis.forEach(a => chart!.removeSeries(a)); build(); return }
  setData()
}, { deep: true })
watch(() => props.baseline, setData, { deep: true })
watch(() => props.markers, applyMarkers, { deep: true })

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  chart?.remove()
  chart = null
})
</script>

<template>
  <div class="rlc">
    <div class="rlc-host-wrap">
      <div ref="chartEl" class="rlc-host" data-testid="risk-chart-host" title="Drag to scroll, wheel to zoom, double-click to reset" @dblclick="resetWindow" />
      <div v-show="tooltip.visible" class="rlc-tooltip" data-testid="risk-chart-tooltip"
        :style="{ left: tooltip.x + 12 + 'px', top: tooltip.y + 12 + 'px' }">
        <div class="rlc-tt-time">{{ tooltip.time }}</div>
        <div v-for="r in tooltip.rows" :key="r.label" class="rlc-tt-row">
          <span class="rlc-swatch" :style="{ background: r.color }"></span>{{ r.label }} <b>{{ r.text }}</b>
        </div>
      </div>
    </div>
    <div class="rlc-legend" data-testid="risk-chart-legend">
      <span v-for="l in legend" :key="l.id" class="rlc-legend-item">
        <span class="rlc-swatch" :style="{ background: l.color }"></span>{{ l.label }} <b>{{ l.last }}</b>
      </span>
      <span v-if="baseline" class="rlc-legend-item rlc-baseline">
        <span class="rlc-swatch dashed" :style="{ borderColor: baseline.color ?? '#2196f3' }"></span>{{ baseline.label ?? 'baseline' }} <b>{{ fmt(baseline.value) }}</b>
      </span>
    </div>
  </div>
</template>

<style scoped>
.rlc { display: flex; flex-direction: column; gap: 0.25rem; }
.rlc-host-wrap { position: relative; }
.rlc-host { width: 100%; }
.rlc-tooltip { position: absolute; pointer-events: none; background: #1c2128; border: 1px solid #333; border-radius: 4px;
  padding: 4px 8px; font-family: 'JetBrains Mono', monospace; font-size: 0.72rem; color: #ccc; z-index: 5; white-space: nowrap; }
.rlc-tt-time { color: #888; margin-bottom: 2px; }
.rlc-tt-row b { color: #fff; font-weight: 600; margin-left: 4px; }
.rlc-legend { display: flex; flex-wrap: wrap; gap: 0.25rem 1rem; font-family: 'JetBrains Mono', monospace; font-size: 0.72rem; color: #888; padding: 0 0.25rem; }
.rlc-legend-item b { color: #ccc; font-weight: 600; margin-left: 4px; }
.rlc-swatch { display: inline-block; width: 10px; height: 3px; margin-right: 6px; vertical-align: middle; border-radius: 1px; }
.rlc-swatch.dashed { background: none; border-top: 1px dashed; height: 0; }
</style>
